/**
 * Bases — Dataview/Bases-style property tables over the vault (Phase 3).
 *
 * BaseView({ baseId?, onOpenNote }):
 *  - baseId undefined -> library screen: all .base files as cards + "New base".
 *  - baseId set -> table screen: config loaded from the .base file's Y.Text
 *    (JSON, see config.ts), evaluated over every live markdown note using the
 *    index worker's entries (frontmatter + tags) and vault file metadata.
 *
 * Inline cell edits rewrite ONLY the target note's frontmatter block via a
 * single Y.Text transact; config edits save debounced as a full JSON replace
 * so bases sync across devices.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { isMarkdownPath, titleFromPath, type VaultFileEntry } from '@rbv/shared';
import { vault } from '../sync/vault.js';
import { index } from '../worker/client.js';
import { useApp } from '../store.js';
import {
  defaultConfig,
  parseConfig,
  propLabel,
  serializeConfig,
  type BaseConfig,
} from './config.js';
import { buildRow, discoverProps, matchGroup, sortRows, type BaseRow } from './evaluate.js';
import { Cell } from './cells.jsx';
import { ConfigEditor } from './ConfigEditor.jsx';
import './bases.css';

export interface BaseViewProps {
  baseId?: string;
  onOpenNote: (id: string) => void;
}

export function BaseView({ baseId, onOpenNote }: BaseViewProps) {
  if (!baseId) return <BaseLibrary />;
  return <BaseTable key={baseId} baseId={baseId} onOpenNote={onOpenNote} />;
}

// ---------------------------------------------------------------- library

const isBasePath = (p: string) => p.endsWith('.base');

function useVaultFilesLocal(): Map<string, VaultFileEntry> {
  const [files, setFiles] = useState<Map<string, VaultFileEntry>>(() => {
    try {
      return vault.filesSnapshot();
    } catch {
      return new Map();
    }
  });
  useEffect(() => vault.onFiles(setFiles), []);
  return files;
}

function BaseLibrary() {
  const files = useVaultFilesLocal();
  const openView = useApp((s) => s.openView);
  const [name, setName] = useState('');
  const [creating, setCreating] = useState(false);

  const bases = useMemo(
    () =>
      [...files.entries()]
        .filter(([, e]) => !e.deleted && isBasePath(e.path))
        .sort((a, b) => a[1].path.localeCompare(b[1].path)),
    [files],
  );

  const create = async () => {
    const trimmed = name.trim();
    if (!trimmed || creating) return;
    setCreating(true);
    try {
      const path = isBasePath(trimmed) ? trimmed : `${trimmed}.base`;
      const id = await vault.createNote(path, serializeConfig(defaultConfig(titleFromPath(path))));
      setName('');
      openView({ kind: 'base', id });
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="base-screen">
      <div className="base-lib">
        <h2 className="base-lib-title">Bases</h2>
        <p className="muted base-lib-sub">
          Property tables over your notes — filters, columns and sorting, saved as{' '}
          <code>.base</code> files in the vault.
        </p>
        <div className="base-new-row">
          <input
            className="base-input"
            value={name}
            placeholder="New base name…"
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void create();
            }}
          />
          <button
            type="button"
            className="base-primary-btn"
            disabled={!name.trim() || creating}
            onClick={() => void create()}
          >
            ＋ New base
          </button>
        </div>
        {bases.length === 0 ? (
          <div className="base-empty">No bases yet. Create one above.</div>
        ) : (
          <div className="base-cards">
            {bases.map(([id, e]) => (
              <button
                type="button"
                className="base-card"
                key={id}
                onClick={() => openView({ kind: 'base', id })}
              >
                <span className="base-card-name">{titleFromPath(e.path)}</span>
                <span className="base-card-path muted">{e.path}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ------------------------------------------------------------- data hook

/** All live markdown notes as evaluated rows; refreshed (batched) whenever
 * the vault file map or the index worker's content changes. null = loading. */
function useBaseRows(): BaseRow[] | null {
  const [rows, setRows] = useState<BaseRow[] | null>(null);

  useEffect(() => {
    let alive = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let fetching = false;
    let again = false;

    const refresh = async () => {
      if (fetching) {
        again = true;
        return;
      }
      fetching = true;
      try {
        do {
          again = false;
          const files = [...vault.filesSnapshot().entries()].filter(
            ([, e]) => !e.deleted && e.kind === 'note' && isMarkdownPath(e.path),
          );
          const notes = await Promise.all(files.map(([id]) => index.note(id)));
          if (!alive) return;
          setRows(files.map(([id, e], i) => buildRow(id, e, notes[i])));
        } while (again);
      } finally {
        fetching = false;
      }
    };

    const schedule = () => {
      clearTimeout(timer);
      timer = setTimeout(() => void refresh(), 150);
    };

    const offFiles = vault.onFiles(schedule);
    const offIndex = index.onChange(schedule);
    void refresh();
    return () => {
      alive = false;
      clearTimeout(timer);
      offFiles();
      offIndex();
    };
  }, []);

  return rows;
}

// ---------------------------------------------------------------- table

function BaseTable({ baseId, onOpenNote }: { baseId: string; onOpenNote: (id: string) => void }) {
  const rows = useBaseRows();
  const [cfgText, setCfgText] = useState<string | null>(() => vault.ytext(baseId)?.toString() ?? null);
  const [config, setConfig] = useState<BaseConfig | null>(null);
  const [invalid, setInvalid] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const [pages, setPages] = useState(1);
  const [overrides, setOverrides] = useState<Map<string, unknown>>(new Map());
  const lastWritten = useRef<string | null>(null);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const pendingSave = useRef<BaseConfig | null>(null);

  const entry = vault.getEntry(baseId);
  const missing = !entry || !!entry.deleted;

  // Config text: initial read + live remote updates (fires on doc load too).
  useEffect(() => {
    const yt = vault.ytext(baseId);
    if (yt) setCfgText(yt.toString());
    return vault.onNoteContent((id, content) => {
      if (id === baseId) setCfgText(content);
    });
  }, [baseId]);

  // Parse whenever text changes (skipping the echo of our own save).
  useEffect(() => {
    if (cfgText === null) return;
    if (lastWritten.current !== null && cfgText === lastWritten.current) return;
    if (cfgText.trim() === '') {
      setConfig(defaultConfig(vault.titleOf(baseId)));
      setInvalid(false);
      return;
    }
    const parsed = parseConfig(cfgText);
    setConfig(parsed);
    setInvalid(parsed === null);
  }, [cfgText, baseId]);

  const flushSave = () => {
    clearTimeout(saveTimer.current);
    const next = pendingSave.current;
    pendingSave.current = null;
    if (!next) return;
    const yt = vault.ytext(baseId);
    const doc = vault.noteDoc(baseId);
    if (!yt || !doc) return;
    const json = serializeConfig(next);
    if (yt.toString() === json) return;
    lastWritten.current = json;
    doc.transact(() => {
      yt.delete(0, yt.length);
      yt.insert(0, json);
    });
  };
  const flushRef = useRef(flushSave);
  flushRef.current = flushSave;
  // Flush any pending config write on unmount / base switch.
  useEffect(() => () => flushRef.current(), [baseId]);

  const updateConfig = (next: BaseConfig) => {
    setConfig(next);
    setInvalid(false);
    pendingSave.current = next;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => flushRef.current(), 500);
  };

  // Clear optimistic cell overrides once fresh rows arrive.
  useEffect(() => {
    setOverrides((prev) => (prev.size > 0 ? new Map() : prev));
  }, [rows]);

  const effectiveRows = useMemo(() => {
    if (!rows || overrides.size === 0) return rows;
    return rows.map((r) => {
      let patched: BaseRow | null = null;
      for (const [key, value] of overrides) {
        const sep = key.indexOf(' ');
        if (key.slice(0, sep) !== r.id) continue;
        const prop = key.slice(sep + 1);
        if (!patched) patched = { ...r, props: { ...r.props } };
        patched.props[prop] = value;
      }
      return patched ?? r;
    });
  }, [rows, overrides]);

  const fmProps = useMemo(() => (effectiveRows ? discoverProps(effectiveRows) : []), [effectiveRows]);

  const filtered = useMemo(() => {
    if (!effectiveRows || !config) return null;
    return sortRows(
      effectiveRows.filter((r) => matchGroup(config.filters, r)),
      config.sort,
    );
  }, [effectiveRows, config]);

  // Reset paging when the base or its filters change.
  const filterKey = config ? JSON.stringify(config.filters) : '';
  useEffect(() => setPages(1), [baseId, filterKey]);

  if (missing) {
    return <div className="muted base-pad">Base not found.</div>;
  }
  if (cfgText === null || (config === null && !invalid)) {
    return (
      <div className="base-screen">
        <div className="spinner" />
      </div>
    );
  }
  if (invalid || config === null) {
    return (
      <div className="base-pad">
        <p className="muted">
          This <code>.base</code> file does not contain a valid base config (JSON).
        </p>
        <button
          type="button"
          className="base-primary-btn"
          onClick={() => updateConfig(defaultConfig(vault.titleOf(baseId)))}
        >
          Reset to default config
        </button>
      </div>
    );
  }

  const pageSize = config.pageSize ?? 50;
  const visible = filtered ? filtered.slice(0, pageSize * pages) : [];
  const onEdited = (noteId: string, prop: string, value: unknown) =>
    setOverrides((prev) => {
      const next = new Map(prev);
      next.set(`${noteId} ${prop}`, value);
      return next;
    });

  return (
    <div className="base-screen">
      <div className="base-head">
        <span className="base-head-name">{config.name || vault.titleOf(baseId)}</span>
        <button
          type="button"
          className={`base-mini-btn wide${showConfig ? ' active' : ''}`}
          onClick={() => setShowConfig(!showConfig)}
        >
          ⚙ Configure
        </button>
      </div>

      {showConfig && <ConfigEditor config={config} fmProps={fmProps} onChange={updateConfig} />}

      {filtered === null ? (
        <div className="spinner" />
      ) : filtered.length === 0 ? (
        <div className="base-empty">
          {rows && rows.length === 0
            ? 'No notes in the vault yet.'
            : 'No notes match the current filters.'}
        </div>
      ) : (
        <div className="base-table-wrap">
          <table className="base-table">
            <thead>
              <tr>
                {config.columns.map((c) => (
                  <th key={c.prop} style={c.width ? { minWidth: c.width, width: c.width } : undefined}>
                    {propLabel(c.prop)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visible.map((row) => (
                <tr key={row.id} onClick={() => onOpenNote(row.id)}>
                  {config.columns.map((c) => (
                    <td key={c.prop}>
                      <Cell row={row} prop={c.prop} onOpenNote={onOpenNote} onEdited={onEdited} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="base-footer">
        <span>
          {filtered ? filtered.length : 0} of {rows ? rows.length : 0} notes
        </span>
        <span style={{ flex: 1 }} />
        {filtered && filtered.length > visible.length && (
          <button type="button" className="base-mini-btn wide" onClick={() => setPages(pages + 1)}>
            Show more ({filtered.length - visible.length} hidden)
          </button>
        )}
      </div>
    </div>
  );
}
